// src/dominio/moneda.js
// Moneda del gimnasio. Cada gym tiene una sola (gimnasios.config.moneda, la
// elige el superadmin); por defecto soles. Los importes guardados no cambian:
// solo cómo se muestran. Mercado Pago (Perú) y la facturación SUNAT solo
// operan en soles: con otra moneda quedan desactivados (también en el backend).
//
// La moneda activa es la del gym abierto en esta sesión (GymContext, o el
// catálogo en la tienda pública). Así cualquier texto con importes —también
// mensajes de validación y de WhatsApp— usa el símbolo correcto sin tener que
// recibirlo por parámetro.

export const MONEDA_POR_DEFECTO = 'PEN';

// Símbolo con el que se muestra cada importe. Las que no usan céntimos se
// muestran sin decimales.
export const MONEDAS = Object.freeze({
  PEN: { simbolo: 'S/', nombre: 'Sol peruano', decimales: 2 },
  USD: { simbolo: 'US$', nombre: 'Dólar estadounidense', decimales: 2 },
  EUR: { simbolo: '€', nombre: 'Euro', decimales: 2 },
  MXN: { simbolo: 'MX$', nombre: 'Peso mexicano', decimales: 2 },
  COP: { simbolo: 'COL$', nombre: 'Peso colombiano', decimales: 0 },
  CLP: { simbolo: 'CLP$', nombre: 'Peso chileno', decimales: 0 },
  ARS: { simbolo: 'AR$', nombre: 'Peso argentino', decimales: 2 },
  BOB: { simbolo: 'Bs', nombre: 'Boliviano', decimales: 2 },
  GTQ: { simbolo: 'Q', nombre: 'Quetzal', decimales: 2 },
  CRC: { simbolo: '₡', nombre: 'Colón costarricense', decimales: 0 },
  DOP: { simbolo: 'RD$', nombre: 'Peso dominicano', decimales: 2 },
  PYG: { simbolo: '₲', nombre: 'Guaraní', decimales: 0 },
  UYU: { simbolo: '$U', nombre: 'Peso uruguayo', decimales: 2 },
});

export const esMonedaValida = (codigo) => Object.prototype.hasOwnProperty.call(MONEDAS, codigo);
export const normalizarMoneda = (codigo) => (esMonedaValida(codigo) ? codigo : MONEDA_POR_DEFECTO);

let activa = MONEDA_POR_DEFECTO;

/** Fija la moneda de la sesión (el gym abierto). Un código desconocido vuelve a soles. */
export const establecerMoneda = (codigo) => { activa = normalizarMoneda(codigo); return activa; };
export const monedaActual = () => activa;
export const simboloMoneda = (codigo = activa) => MONEDAS[normalizarMoneda(codigo)].simbolo;
/** Pagos en línea (Mercado Pago Perú) y facturación SUNAT: solo en soles. */
export const operaEnSoles = (codigo = activa) => normalizarMoneda(codigo) === 'PEN';

/**
 * Importe con símbolo y separador de miles: «S/ 1,150.00», «COL$ 85,000».
 * @param {number|string} monto
 * @param {{ codigo?: string, decimales?: number }} [opciones]
 */
export const formatearDinero = (monto, { codigo = activa, decimales } = {}) => {
  const moneda = MONEDAS[normalizarMoneda(codigo)];
  const digitos = decimales ?? moneda.decimales;
  const numero = (Number(monto) || 0).toLocaleString('en-US', { minimumFractionDigits: digitos, maximumFractionDigits: digitos });
  return `${moneda.simbolo} ${numero}`;
};
export const formatearCentimos = (centimos, opciones) => formatearDinero((Number(centimos) || 0) / 100, opciones);
